/**
 * AI:
 * Status: "in progress"
 * Type: Widget
 * SubType: WidgetConfig
 * Reason: Data-widget builder for the „Basisdaten" of ONE `cr.CustomerBankAccount` in the read-only bank
 *         account overview OBC (`CrCustomerBankAccountOverviewObjectComponent`, requirement §5.3). A single
 *         object card (`uiComponent: 'data'`, `createWidgetEntityData`) with a curated, ordered set of 17
 *         fields - a reading surface, deliberately NOT an editing form (decision mm, 06.10.2026: bank
 *         accounts change only through the wizards).
 *
 * ── THE 17 FIELDS AND WHERE THEIR IDS COME FROM (AU-3)
 *
 *   The ids are the keys of `form.formFields` that the generic GET `/mvsa/cr/customerBankAccounts/{id}`
 *   returns - the very call the data widget makes (`CoreDsImplEntityService.getDataObject` →
 *   `MvsCrudService.get`). Checked live on dev/tenantA (bank account 1330, 17.10.2026): all 17 ids are
 *   present in that form. A wrong id would drop the field SILENTLY (`WidgetService.buildUiData` skips a
 *   layout field it cannot find in the form, TW-6), hence the spec pins every id and its order.
 *
 *   The two REFERENCE fields are configured as `…DtoName`, not `…DtoId`: that is the framework's display
 *   convention for a ManyToOne (the default widget form skips every relationship field except `…DtoName`,
 *   `widget.service.ts` `addDefaultFormFieldsToLayout`; `buildUiData` gives the `…DtoName` column the object
 *   type of its `…DtoId` sibling). A `…DtoId` id would print the bare number.
 *
 *   The enum fields (`bankAccountStatus`, `sepaMandateStatus`) carry their value list in that form, so the
 *   text comes from the backend - there is no text map in TypeScript (FD-5).
 *
 *   The IBAN is shown IN FULL: the field carries no output override, the widget prints the stored value
 *   (Q-11c, user 28.09.2026; FD-8).
 *
 * ── WHAT THE GENERIC GET DOES NOT DO
 *
 *   `CustomerBankAccountForm.postFormGenerate` (hide the mandate block without a mandate, hide the backend-
 *   owned `sepaMandateStatus` / `sepaMandateRevokedDate`) is NOT reflected in the form of the generic GET:
 *   live, every one of the 17 fields arrives with `access: update`. The data widget therefore shows all 17
 *   whatever the mandate state; empty values render as the framework placeholder.
 *
 * ── DISPLAY ONLY (FD-3, AU-2)
 *
 *   The four FIXED parameters of `buildDisplayOnlyListWidgetParams()` (formEdit / formCopy /
 *   allowDoubleClick = false, objectWidget = 'data') are IMPORTED from the customer navigation - never a
 *   second copy. The host additionally sets `[disableUpdate]="true"` on the `<mvs-widget>`.
 *
 * ── ALIASES ARE IDENTITY (FD-6, AU-13)
 *
 *   One alias per variant; `ui.UiWidget` / `ui.UiWidgetLayout` hang off it. Never rename after the first
 *   deploy. A stored `ui.UiWidgetLayout` on one of the aliases REPLACES the code `widgetForm` below
 *   (CANNOT 8) - none is shipped.
 *
 * @ticket TM-1000002030
 */
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetForm} from 'features/core/shared/widget/widget-form';
import {WidgetConfigFormLayout} from 'features/core/shared/widget/widget-config-form-layout';
import {WidgetConfigFormField} from 'features/core/shared/widget/widget-config-form-field';
import {
    buildDisplayOnlyListWidgetParams
} from '../component/protected-components/cr-customer/component/cr-customer-standard-navigation/cr-customer-standard-navigation.component';

/** Widget alias of the full variant. Identity - NEVER rename (FD-6). @ticket TM-1000002030 */
export const CR_BANK_ACCOUNT_BASE_DATA_ALIAS_FULL = 'cr.customerBankAccount.object.data.full';

/** Widget alias of the side-drawer variant. Identity - NEVER rename (FD-6). @ticket TM-1000002030 */
export const CR_BANK_ACCOUNT_BASE_DATA_ALIAS_SIDE = 'cr.customerBankAccount.object.data.side';

/** The entity the card reads (generic GET). @ticket TM-1000002030 */
export const CR_BANK_ACCOUNT_BASE_DATA_OBJECT_TYPE = 'cr.CustomerBankAccount';

/**
 * One curated field of the card: the form field id and its German label.
 *
 * @ticket TM-1000002030
 */
export interface CrBankAccountBaseDataField {
    readonly field: string;
    readonly label: string;
}

/**
 * The 17 base-data fields in display order (requirement §5.3), each id verified against the live form of
 * the generic GET (AU-3). „Gültig ab/bis" is the validity of the BANK ACCOUNT itself, not the assignment
 * period of the table („Zugeordnet ab/bis", business rule 6).
 *
 * @ticket TM-1000002030
 */
export const CR_BANK_ACCOUNT_BASE_DATA_FIELDS: readonly CrBankAccountBaseDataField[] = [
    {field: 'iban', label: 'IBAN'},
    {field: 'bic', label: 'BIC'},
    {field: 'bankName', label: 'Bank'},
    {field: 'bankBranchName', label: 'Filiale'},
    {field: 'accountHolderName', label: 'Kontoinhaber'},
    {field: 'deviantAccountHolder', label: 'Abweichender Kontoinhaber'},
    {field: 'deviantAccountHolderPersonDtoName', label: 'Kontoinhaber (Person)'},
    {field: 'bankAccountStatus', label: 'Kontostatus'},
    {field: 'startDate', label: 'Gültig ab'},
    {field: 'endDate', label: 'Gültig bis'},
    {field: 'verifiedDate', label: 'Prüfdatum'},
    {field: 'sepaMandate', label: 'SEPA-Mandat vorhanden'},
    {field: 'sepaMandateStatus', label: 'Mandatsstatus'},
    {field: 'sepaMandateIdentifier', label: 'Mandatsreferenz'},
    {field: 'sepaDateOfSignature', label: 'Unterschrieben am'},
    {field: 'sepaMandateSourceDtoName', label: 'Mandatsquelle'},
    {field: 'sepaMandateRevokedDate', label: 'Widerrufen am'},
];

/**
 * The curated layout of the card, passed as the `widgetForm` parameter: exactly the 17 fields of
 * {@link CR_BANK_ACCOUNT_BASE_DATA_FIELDS}, in that order, with their German labels. `hideFields = true`
 * plus `fieldsConfigured = true` is the default-layout convention: only the configured fields are shown.
 * A fresh instance per call.
 *
 * @ticket TM-1000002030
 */
export function buildCrBankAccountBaseDataWidgetForm(): WidgetForm {

    const layout = new WidgetConfigFormLayout();
    layout.hideFields = true;
    layout.fieldsConfigured = true;

    CR_BANK_ACCOUNT_BASE_DATA_FIELDS.forEach((column, index) => {
        const field = WidgetConfigFormField.create(column.field, '', '');
        field.uiLabel = column.label;
        field.uiPriority = index;
        layout.addField(field);
    });

    const widgetForm = new WidgetForm();
    widgetForm.setDefaultLayout(layout);
    return widgetForm;
}

/**
 * The „Basisdaten" data widget of one bank account.
 *
 * @param bankAccountId id of the `cr.CustomerBankAccount`; `null`/`undefined`/`0` returns `null` - an id of
 *                      0 would put the data widget into its no-object state
 * @param compact       `true` for the side drawer (one column, side alias), `false` for the full view (two
 *                      columns, full alias)
 * @returns the widget, or `null` without a usable id
 * @ticket TM-1000002030
 */
export function buildCrBankAccountBaseDataWidget(bankAccountId: number | null | undefined,
                                                 compact: boolean): WidgetData | null {

    if (!bankAccountId) {
        return null;
    }

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: Read-only „Basisdaten" card of ONE cr.CustomerBankAccount (IBAN unmasked, bank, holder, status,
     *         validity, SEPA mandate) in the bank account overview OBC - curated 17-field widgetForm, two
     *         columns in the full view and one in the side drawer, display-only FIXED params (FD-3).
     */
    return WidgetFactory.createWidgetEntityData(
        compact ? CR_BANK_ACCOUNT_BASE_DATA_ALIAS_SIDE : CR_BANK_ACCOUNT_BASE_DATA_ALIAS_FULL,
        'Basisdaten',
        CR_BANK_ACCOUNT_BASE_DATA_OBJECT_TYPE,
        bankAccountId,
        WidgetDataParam.create('size', 'S'),
        WidgetDataParam.create('columns', compact ? 1 : 2),
        WidgetDataParam.create('widgetForm', buildCrBankAccountBaseDataWidgetForm()),
        ...buildDisplayOnlyListWidgetParams());
}
